import styled, { css } from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { BorderRadiusMedium, Spacing16, Spacing24, Spacing48 } from 'autogen/design-tokens/tokens';
import ScrollSnapSlider, { ScrollSnapSliderContext, useScrollSnapSlider } from 'components/generic/ScrollSnapSlider';
import NavigationFadeButton from 'components/generic/ScrollSnapSlider/components/NavigationFadeButton';
import { Headline } from 'components/generic/typography';
import { filterPublishedFields } from 'components/pages/Contentful/shared/ContentfulHelpers';
import dynamic from 'helpers/dynamic';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import type { TypeShortcutsDetailed } from 'types/autogen/contentful';
import { breakpoint, sizes } from 'variables';
import Item from './components/Item';
import { gap, sliderPadding, widthFormulaLG, widthFormulaMD, widthFormulaSM, widthFormulaXS } from './shared/styles';

// #region Styles
const Wrapper = styled.div`
    position: relative;
`;

const Header = styled(Headline)`
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        padding: 0;
        margin-bottom: ${Spacing24};
    }
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)<{ itemCount: number }>`
    z-index: -1;
    && {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: auto;
        gap: ${gap};
        padding: 0 ${sliderPadding};
        scroll-padding-inline-end: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};
        scroll-snap-type: none;
        scroll-snap-stop: normal;

        grid-template-columns: repeat(auto-fit, ${widthFormulaXS});

        ${breakpoint.up(sizes.sm)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaSM});
        }

        ${breakpoint.up(sizes.md)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaMD});
        }

        ${breakpoint.up(sizes.lg)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaLG});
        }

        ${breakpoint.up(sizes.xl)} {
            padding: 0;
            scroll-padding-inline-end: 0;
            scroll-padding-inline-start: 0;
            scroll-snap-type: x mandatory;
            scroll-snap-stop: always;
            grid-template-columns: repeat(auto-fit, 112px);
        }

        ${breakpoint.up(sizes.xxl)} {
            grid-template-columns: repeat(auto-fit, 116px);
        }
    }

    ${invisibleScrollbar()}
`;

const Container = styled.div<{ itemCount: number }>`
    position: relative;
    display: flex;
    margin: 0px -${Spacing16};
    z-index: 0;
`;

const navButtonStyling = css`
    display: none;

    ${breakpoint.up(sizes.xl)} {
        display: block;
        position: absolute;
        top: ${Spacing48};
        padding: ${Spacing16};
        border-radius: ${BorderRadiusMedium};
        transform: translateY(-50%);

        & svg {
            height: ${Spacing16};
            width: ${Spacing16};
        }
    }
`;

const StyledNavigationButtonLeft = styled(NavigationFadeButton)`
    ${navButtonStyling}

    ${breakpoint.up(sizes.xl)} {
        left: 0;
    }
`;

const StyledNavigationButtonRight = styled(NavigationFadeButton)`
    ${navButtonStyling}

    ${breakpoint.up(sizes.xl)} {
        right: 0;
    }
`;
// #endregion Styles

const sliderOptions = {
    draggable: true,
    loop: false
};

export default function Shortcuts(props: TypeShortcutsDetailed): JSX.Element {
    const {
        fields: { headline, links, trackingProperties },
        sys: { id }
    } = props;
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const { ref, slider } = useScrollSnapSlider(sliderOptions);

    const linksToRender = filterPublishedFields(links);

    return (
        <ScrollSnapSliderContext.Provider value={slider}>
            <Wrapper {...inspectorProps({ fieldId: 'internalName' })}>
                {headline && <Header size="L">{headline}</Header>}
                {!!linksToRender?.length && (
                    <Container itemCount={linksToRender.length}>
                        <StyledNavigationButtonLeft
                            direction="prev"
                            slideAmount={7}
                            variant="ghost"
                            size="L"
                            withBorder
                        />
                        <StyledNavigationButtonRight
                            direction="next"
                            slideAmount={7}
                            variant="ghost"
                            size="L"
                            withBorder
                        />
                        <StyledScrollSnapSlider ref={ref} itemCount={linksToRender.length}>
                            {linksToRender.map((link, index) => (
                                <Item
                                    link={link}
                                    trackingProperties={trackingProperties?.fields}
                                    key={index}
                                    data-slide-index={index}
                                />
                            ))}
                        </StyledScrollSnapSlider>
                    </Container>
                )}
            </Wrapper>
        </ScrollSnapSliderContext.Provider>
    );
}
